import dayjs from "dayjs";
import { resolveAiImpactAssessmentReportUri } from "../utils/manualAssessmentReportShare";
import { apiClient, type ApiResponse } from "./apiClient";
import {
  buildConceptAssessmentResult,
  coerceToTriageImpactedItems,
  type RegulationAssessmentResultPayload,
  type TriageImpactedItem,
} from "./manualAssessmentService";

export type { RegulationAssessmentResultPayload };

export function buildConceptAssessmentResultFromCenterRow(
  row: Pick<
    AiImpactAssessmentGridRow,
    | "regulationId"
    | "version"
    | "impacted"
    | "impactedLevelApi"
    | "reportUrl"
    | "impactedItems"
  >,
): RegulationAssessmentResultPayload {
  return buildConceptAssessmentResult(
    row.regulationId,
    row.version,
    row.impacted,
    row.impactedLevelApi,
    row.reportUrl,
    row.impactedItems,
  );
}
import { formatAssessmentHistoryStatus } from "../utils/assessmentHistoryStatus";

export interface AssessmentCenterImpactedItem {
  type: string;
  keyword?: string | null;
  concept?: {
    name: string;
    code: string;
    description?: string;
  } | null;
  concept_value?: {
    name: string;
    code: string;
    description?: string;
  } | null;
  impacted?: boolean;
  impacted_level?: string;
}

export interface AssessmentCenterApiItem {
  bundle_task_uuid: string;
  impacted: boolean;
  impacted_level: string;
  status: string;
  executed_at: string;
  executor_name: string;
  report_url: string | null;
  /** Present when the row also carries workflow-style references (AI + Manual names). */
  references?: Array<{
    uri?: string | null;
    name?: string | null;
    content_type?: string | null;
  }> | null;
  by: string;
  impacted_items: AssessmentCenterImpactedItem[] | null;
  /** Present when status is partial — concept names that failed to process. */
  failed_concept_names?: string[] | null;
  regulation_id: string;
  regulation_version: string;
  regulation_title: string | null;
}

/** List response: `data` is the page; `total` / `page` / `page_size` are siblings (not nested). */
export type AssessmentCenterListApiResponse = ApiResponse<AssessmentCenterApiItem[]> & {
  total?: number;
  page?: number;
  page_size?: number;
  /** Some deployments may also return dropdown filter options alongside the list. */
  filter_options?: AssessmentFilterOptionsResponse;
  filterOptions?: AssessmentFilterOptionsResponse;
};

export interface GetAssessmentCenterParams {
  status?: string[];
  executed_by?: string;
  date?: string;
  impacted_level?: string[];
  by?: string[];
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface AssessmentCenterListResult {
  items: AssessmentCenterApiItem[];
  total: number;
  page: number;
  pageSize: number;
  /** Optional dropdown filter options returned by list endpoint. */
  filterOptions?: AssessmentFilterOptionsResponse;
}

export type AiImpactAssessmentGridRow = {
  id: string;
  taskUuid: string;
  regulationId: string;
  regulation: string;
  version: string;
  executedDate: string;
  /** Raw API status — actions visibility, re-run, etc. */
  rawStatus: string;
  /** Formatted status label (Assessment History parity). */
  status: string;
  executedBy: string;
  assessmentType: string;
  impactedConcepts: string;
  impactLevel: string;
  reportUrl?: string | null;
  /** From API `failed_concept_names` — used for partial-status hover in Assessment Center. */
  failedConceptNames?: string[];
  /** IA `impacted_items` for manual triage recommendation. */
  impactedItems: TriageImpactedItem[];
  /** Raw API `impacted` — `concept_assessment_result`. */
  impacted: boolean;
  /** Raw API `impacted_level` — `concept_assessment_result`. */
  impactedLevelApi: string;
};

function formatImpactedItems(items: AssessmentCenterImpactedItem[] | null): string {
  if (!items?.length) return "—";
  return items
    .map((item) => {
      if (item.type === "keyword" && item.keyword) return item.keyword;
      if (item.concept_value?.name) return item.concept_value.name;
      if (item.concept?.name) return item.concept.name;
      return item.type;
    })
    .join(", ");
}

export function mapAssessmentCenterItemToGridRow(
  item: AssessmentCenterApiItem,
): AiImpactAssessmentGridRow {
  const executed = item.executed_at
    ? dayjs(item.executed_at).format("DD-MMM-YYYY")
    : "—";

  const failedNames = Array.isArray(item.failed_concept_names)
    ? item.failed_concept_names.filter(
        (n): n is string => typeof n === "string" && n.trim().length > 0,
      )
    : [];

  return {
    id: `${item.bundle_task_uuid}:${item.regulation_id}:${item.regulation_version}:${item.executed_at}`,
    taskUuid: item.bundle_task_uuid,
    regulationId: item.regulation_id,
    regulation:
      item.regulation_title?.trim() ||
      item.regulation_id?.trim() ||
      "—",
    version: item.regulation_version,
    executedDate: executed,
    rawStatus: item.status?.trim() || "",
    status: formatAssessmentHistoryStatus(item.status || ""),
    executedBy: item.executor_name?.trim() || "—",
    assessmentType: item.by?.trim() || "—",
    impactedConcepts: formatImpactedItems(item.impacted_items),
    impactLevel: item.impacted_level ?? "—",
    reportUrl:
      resolveAiImpactAssessmentReportUri(
        item as unknown as Record<string, unknown>,
      ) || null,
    impactedItems: coerceToTriageImpactedItems(item.impacted_items),
    impacted: Boolean(item.impacted),
    impactedLevelApi: (item.impacted_level ?? "no").trim() || "no",
    ...(failedNames.length > 0 ? { failedConceptNames: failedNames } : {}),
  };
}

function normalizeFilterOptionsFromUnknown(
  maybe: unknown,
): AssessmentFilterOptionsResponse | undefined {
  if (!Array.isArray(maybe) || maybe.length === 0) return undefined;
  const first = (maybe as unknown[])[0];
  if (!first || typeof first !== "object") return undefined;
  const firstObj = first as { name?: unknown; options?: unknown };
  if (typeof firstObj.name !== "string") return undefined;
  if (!Array.isArray(firstObj.options)) return undefined;
  return maybe as AssessmentFilterOptionsResponse;
}

/**
 * GET /assessment/histories — paginated AI / bundle impact assessments for Assessment Center.
 */
export async function getAssessmentCenterList(
  params: GetAssessmentCenterParams = {},
): Promise<AssessmentCenterListResult> {
  const page = params.page ?? 1;
  const page_size = params.page_size ?? 20;
  const sort_by = params.sort_by ?? "executed_date";
  const sort_order = params.sort_order ?? "desc";

  const buildQueryParams = (): Record<string, any> => {
    const q: Record<string, any> = { page, page_size };
    if (params.status?.length) q.status = params.status;
    if (params.executed_by) q.executed_by = params.executed_by;
    if (params.date) q.date = params.date;
    if (params.impacted_level?.length) q.impacted_level = params.impacted_level;
    if (params.by?.length) q.by = params.by;
    q.sort_by = sort_by;
    q.sort_order = sort_order;
    return q;
  };

  const result = (await apiClient.get<unknown>(
    "/assessment/histories",
    buildQueryParams(),
  )) as AssessmentCenterListApiResponse;

  const data = result.data as unknown;
  const dataObj =
    data && typeof data === "object" ? (data as Record<string, unknown>) : null;

  // Swagger shape: { data: AssessmentCenterApiItem[], total, page, page_size }
  // Alternative shape: { data: { items, total, page, page_size, filter_options } }
  let items: AssessmentCenterApiItem[] = [];
  if (Array.isArray(data)) {
    items = data as AssessmentCenterApiItem[];
  } else if (Array.isArray((dataObj as any)?.items)) {
    items = (((dataObj as any).items ?? []) as AssessmentCenterApiItem[]) ?? [];
  }

  let totalCandidate: number | undefined;
  if (typeof result.total === "number") totalCandidate = result.total;
  else if (typeof (dataObj as any)?.total === "number") {
    totalCandidate = (dataObj as any).total;
  }

  let pageCandidate: number | undefined;
  if (typeof result.page === "number") pageCandidate = result.page;
  else if (typeof (dataObj as any)?.page === "number") {
    pageCandidate = (dataObj as any).page;
  }

  let pageSizeCandidate: number | undefined;
  if (typeof result.page_size === "number") pageSizeCandidate = result.page_size;
  else if (typeof (dataObj as any)?.page_size === "number") {
    pageSizeCandidate = (dataObj as any).page_size;
  }

  const total = typeof totalCandidate === "number" ? totalCandidate : items.length;
  const pageNum = typeof pageCandidate === "number" ? pageCandidate : page;
  const pageSize =
    typeof pageSizeCandidate === "number" ? pageSizeCandidate : page_size;

  const filterOptions =
    normalizeFilterOptionsFromUnknown((result as any).filter_options) ??
    normalizeFilterOptionsFromUnknown((result as any).filterOptions) ??
    normalizeFilterOptionsFromUnknown((dataObj as any)?.filter_options) ??
    normalizeFilterOptionsFromUnknown((dataObj as any)?.filterOptions);

  return {
    items,
    total,
    page: pageNum,
    pageSize,
    filterOptions,
  };
}

export type AssessmentFilterOptionsResponse = Array<{
  name: string;
  options: Array<{ name: string; value: string | string[] }>;
}>;

/**
 * GET /assessment/histories/filter-options
 * Assessment Center AI filter dropdown option source (same shape as Assessment History filter-options).
 */
export async function getAssessmentCenterAiFilterOptions(): Promise<AssessmentFilterOptionsResponse> {
  const result = await apiClient.get<AssessmentFilterOptionsResponse>(
    "/assessment/histories/filter-options",
  );
  return Array.isArray(result.data) ? result.data : [];
}
